/**
 * DEV Agent Overrides
 * 
 * DEV/INTERNAL USE ONLY
 * 
 * Provides session-scoped overrides for agent eligibility during development
 * and testing. These overrides do NOT persist to the database and do NOT
 * affect simulation engine results.
 * 
 * Usage:
 *   import { DEV_AGENT_OVERRIDES, applyDevOverrides } from '@/lib/dev/agentOverrides';
 */

import { isDevMode } from '@/lib/featureFlags';
import type { MockAgent } from '@/contexts/MockAgentsContext';

/**
 * DEV Override configuration object.
 * All values are session-scoped and reset on page reload.
 */
export interface DevAgentOverrideConfig {
  /** Master toggle - must be true for any overrides to apply */
  enabled: boolean;
  /** Force all agents to pass eligibility checks */
  forceAllAgentsEligible: boolean;
  /** Override wallet balance for all agents (undefined = use real value) */
  forceWalletBalance?: number;
  /** Override licensed states for all agents (undefined = use real value) */
  forceLicenses?: string[];
}

/**
 * Default (disabled) configuration.
 * CRITICAL: enabled MUST default to false.
 */
const DEFAULT_OVERRIDES: DevAgentOverrideConfig = {
  enabled: false,
  forceAllAgentsEligible: false,
  forceWalletBalance: undefined,
  forceLicenses: undefined,
};

/**
 * Session-scoped overrides object.
 * Mutable at runtime but never persisted.
 */
export const DEV_AGENT_OVERRIDES: DevAgentOverrideConfig = { ...DEFAULT_OVERRIDES };

/**
 * Update the override configuration.
 * Only works in development mode.
 */
export function setDevOverrides(updates: Partial<DevAgentOverrideConfig>): void {
  if (!isDevMode) {
    console.warn('[DEV Overrides] Cannot set overrides outside development mode');
    return;
  }
  
  Object.assign(DEV_AGENT_OVERRIDES, updates);
  
  if (DEV_AGENT_OVERRIDES.enabled) {
    console.log('[DEV Overrides] Configuration updated:', DEV_AGENT_OVERRIDES);
  }
}

/**
 * Reset all overrides to defaults.
 */
export function resetDevOverrides(): void {
  Object.assign(DEV_AGENT_OVERRIDES, DEFAULT_OVERRIDES);
  console.log('[DEV Overrides] Reset to defaults');
}

/**
 * Check if DEV overrides are currently active.
 * Overrides are only active when:
 * - isDevMode is true (environment check)
 * - DEV_AGENT_OVERRIDES.enabled is true (explicit toggle)
 */
export function areDevOverridesActive(): boolean {
  return isDevMode && DEV_AGENT_OVERRIDES.enabled;
}

/**
 * Apply DEV overrides to an agent before eligibility filtering.
 * Returns a modified copy of the agent (does not mutate original).
 * 
 * This should be called AFTER agent loading but BEFORE eligibility filtering
 * in the routing orchestrator.
 */
export function applyDevOverridesToAgent(agent: MockAgent): MockAgent {
  if (!areDevOverridesActive()) {
    return agent;
  }
  
  const overriddenAgent = { ...agent };
  
  // Override wallet balance
  if (DEV_AGENT_OVERRIDES.forceWalletBalance !== undefined) {
    overriddenAgent.walletBalance = DEV_AGENT_OVERRIDES.forceWalletBalance;
  }
  
  // Override licensed states
  if (DEV_AGENT_OVERRIDES.forceLicenses !== undefined && DEV_AGENT_OVERRIDES.forceLicenses.length > 0) {
    overriddenAgent.licensedStates = DEV_AGENT_OVERRIDES.forceLicenses;
  }
  
  // Force all lead preferences to be active
  if (DEV_AGENT_OVERRIDES.forceAllAgentsEligible && overriddenAgent.leadPreferences) {
    overriddenAgent.leadPreferences = overriddenAgent.leadPreferences.map(pref => ({
      ...pref,
      isActive: true,
    }));
  }
  
  return overriddenAgent;
}

/**
 * Apply DEV overrides to all agents in a pool.
 * Returns a new array with modified copies.
 */
export function applyDevOverridesToPool(agents: MockAgent[]): MockAgent[] {
  if (!areDevOverridesActive()) {
    return agents;
  }
  
  console.log('[DEV Overrides] Applying overrides to', agents.length, 'agents');
  return agents.map(applyDevOverridesToAgent);
}

/**
 * Get a summary of current overrides for display.
 */
export function getDevOverridesSummary(): string[] {
  if (!areDevOverridesActive()) {
    return [];
  }
  
  const summary: string[] = [];
  
  if (DEV_AGENT_OVERRIDES.forceAllAgentsEligible) {
    summary.push('Force All Agents Eligible');
  }
  
  if (DEV_AGENT_OVERRIDES.forceWalletBalance !== undefined) {
    summary.push(`Wallet: $${DEV_AGENT_OVERRIDES.forceWalletBalance.toLocaleString()}`);
  }
  
  if (DEV_AGENT_OVERRIDES.forceLicenses && DEV_AGENT_OVERRIDES.forceLicenses.length > 0) {
    summary.push(`Licenses: ${DEV_AGENT_OVERRIDES.forceLicenses.join(', ')}`);
  }
  
  return summary;
}
